import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LostReasonDialog } from "@/components/enquiries/LostReasonDialog";

export const CUSTOMER_STATUSES = [
  { key: "lead",     label: "Lead",     hint: "New contact, not yet engaged",               tone: "bg-sky-50 text-sky-800 border-sky-200" },
  { key: "prospect", label: "Prospect", hint: "In conversation or Initial Assessment booked", tone: "bg-amber-50 text-amber-900 border-amber-300" },
  { key: "customer", label: "Customer", hint: "Attended or paying",                         tone: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  { key: "lost",     label: "Lost",     hint: "Not proceeding",                             tone: "bg-zinc-100 text-zinc-600 border-zinc-300" },
];

export const customerStatusMeta = (key) => CUSTOMER_STATUSES.find((s) => s.key === key);

export function CustomerStatusChips({ value, counts, onChange }) {
  const chip = (active) => `text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors`
    + (active ? " bg-zinc-950 text-white border-zinc-950" : " bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500");
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="customer-status-chips">
      <span className="text-2xs uppercase tracking-wider text-zinc-500 mr-1">Customer status</span>
      <button onClick={() => onChange("")} className={chip(!value)} data-testid="cs-chip-all">Everyone</button>
      {CUSTOMER_STATUSES.map((s) => (
        <button
          key={s.key}
          onClick={() => onChange(value === s.key ? "" : s.key)}
          className={chip(value === s.key)}
          title={s.hint}
          data-testid={`cs-chip-${s.key}`}
        >
          <span className={`h-2 w-2 rounded-full border ${s.tone}`} />
          {s.label}
          <span className={`font-mono-num text-2xs ${value === s.key ? "text-zinc-300" : "text-zinc-500"}`} data-testid={`cs-chip-count-${s.key}`}>
            {counts?.[s.key] ?? 0}
          </span>
        </button>
      ))}
    </div>
  );
}

export function CustomerStatusBadge({ status, testId, className = "" }) {
  const meta = customerStatusMeta(status);
  if (!meta) return null;
  return (
    <span
      className={`uppercase tracking-wider px-1 py-0.5 rounded border text-2xs font-semibold ${meta.tone} ${className}`}
      data-testid={testId}
    >{meta.label}</span>
  );
}

function provenance(enq) {
  const how = enq.customer_status_source === "manual" ? "Set manually" : "Set automatically";
  const by = enq.customer_status_by_name ? ` by ${enq.customer_status_by_name}` : "";
  const when = enq.customer_status_at ? ` · ${new Date(enq.customer_status_at).toLocaleDateString()}` : "";
  return `${how}${by}${when} — ${enq.customer_status_reason || ""}`;
}

export function CustomerStatusPicker({ enq, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [askLost, setAskLost] = useState(false);
  const meta = customerStatusMeta(enq.customer_status) || CUSTOMER_STATUSES[0];

  async function pick(key, extra = {}) {
    if (key === enq.customer_status && key !== "lost") return;
    if (key === "lost" && !extra.lost_reason) { setAskLost(true); return; }
    setBusy(true);
    try {
      const r = await api.post(`/enquiries/${enq.id}/customer-status`, { status: key, ...extra });
      setAskLost(false);
      const n = r.data?.updated_count || 1;
      const who = enq.contact?.name || "Contact";
      toast.success(`${who} is now ${customerStatusMeta(key).label}`, {
        description: n > 1 ? `Updated across ${n} enquiries from this person` : undefined,
      });
      onChanged?.();
    } catch (e) {
      toast.error("Could not change status", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="customer-status">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-2xs font-semibold uppercase tracking-wider transition-colors hover:brightness-95 ${meta.tone}`}
          disabled={busy}
          data-testid="customer-status-trigger"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {meta.label}
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-2xs text-zinc-500 font-normal">
            Customer status — applies to every enquiry from this person
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {CUSTOMER_STATUSES.map((s) => (
            <DropdownMenuItem
              key={s.key}
              onSelect={() => pick(s.key)}
              className="flex items-start gap-2 cursor-pointer"
              data-testid={`customer-status-option-${s.key}`}
            >
              <Check className={`h-3.5 w-3.5 mt-0.5 ${s.key === enq.customer_status ? "opacity-100" : "opacity-0"}`} />
              <div>
                <div className="text-xs font-medium text-zinc-900">{s.label}</div>
                <div className="text-2xs text-zinc-500">{s.hint}</div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {enq.customer_status && (
        <span className="text-2xs text-zinc-500" data-testid="customer-status-provenance">{provenance(enq)}</span>
      )}
      {askLost && (
        <LostReasonDialog
          name={enq.contact?.name}
          busy={busy}
          onClose={() => setAskLost(false)}
          onSave={(lost_reason, lost_note) => pick("lost", { lost_reason, lost_note })}
        />
      )}
    </div>
  );
}
